import { useEffect, useRef, useState, type RefObject } from "react";
import {
  getGoogleMapsApiKey as defaultGetGoogleMapsApiKey,
  loadGoogleMaps as defaultLoadGoogleMaps,
} from "./AddressAutocomplete";

type GetApiKeyFn = (explicit?: string) => string | undefined;
type LoadMapsFn = (apiKey: string) => Promise<void>;
type MapsAuthFailureWindow = Window &
  typeof globalThis & { gm_authFailure?: () => void };

const mapsAuthFailureSubscribers = new Set<() => void>();
let previousMapsAuthFailureHandler: (() => void) | undefined;
let mapsAuthFailureDispatcher: (() => void) | undefined;

/**
 * Google reports key/authentication failures through a single documented
 * window callback after the Maps script has loaded. Share that callback across
 * mounted map controllers so one instance cannot overwrite another, and
 * restore any pre-existing callback after the last controller unmounts.
 */
function subscribeToMapsAuthFailure(onFailure: () => void): () => void {
  const mapsWindow = window as MapsAuthFailureWindow;

  if (mapsAuthFailureSubscribers.size === 0) {
    previousMapsAuthFailureHandler = mapsWindow.gm_authFailure;
    mapsAuthFailureDispatcher = () => {
      try {
        previousMapsAuthFailureHandler?.();
      } finally {
        for (const subscriber of [...mapsAuthFailureSubscribers]) subscriber();
      }
    };
    mapsWindow.gm_authFailure = mapsAuthFailureDispatcher;
  }

  mapsAuthFailureSubscribers.add(onFailure);

  return () => {
    mapsAuthFailureSubscribers.delete(onFailure);
    if (mapsAuthFailureSubscribers.size > 0) return;

    if (mapsWindow.gm_authFailure === mapsAuthFailureDispatcher) {
      if (previousMapsAuthFailureHandler) {
        mapsWindow.gm_authFailure = previousMapsAuthFailureHandler;
      } else {
        delete mapsWindow.gm_authFailure;
      }
    }
    previousMapsAuthFailureHandler = undefined;
    mapsAuthFailureDispatcher = undefined;
  };
}

/**
 * A facility point the map can place a marker for (facility-discovery B2).
 *
 * Deliberately MINIMAL + decoupled from the backend schema: the caller projects
 * whatever shape its API returns (e.g. `FacilityResponse.address.{latitude,
 * longitude}`) down to this. `latitude`/`longitude` are optional — a facility
 * without a geocoded point is rendered with NO marker (honesty: no pin without a
 * real coordinate), never a fabricated location.
 */
export interface FacilityMapFacility {
  facility_id: string;
  name: string;
  latitude?: number | null;
  longitude?: number | null;
  /**
   * Optional secondary line shown under the name in the marker popup — e.g.
   * "1.4 km away" or the city. Plain text only (rendered via textContent, never
   * innerHTML), so it is safe with untrusted backend strings.
   */
  subtitle?: string | null;
  /** Short pin text when pill markers are on (defaults to the name). */
  pinLabel?: string | null;
  /** Draw this pin in the highlight colour (e.g. a place with an offer). */
  highlight?: boolean;
}

/** Colours for labelled pill pins; the renderer passes its theme values. */
export interface FacilityMapPillColors {
  fill: string;
  stroke: string;
  text: string;
  highlightFill: string;
  highlightText: string;
}

export interface FacilityMapCenter {
  latitude: number;
  longitude: number;
}

export interface FacilityMapProps {
  /** Facilities to pin. Coord-less entries are skipped (no marker). */
  facilities: FacilityMapFacility[];
  /** The point the map opens centered on (the discovery center). */
  center: FacilityMapCenter;
  /** Marker click → the chosen facility (e.g. navigate into its detail). */
  onSelectFacility: (facility: FacilityMapFacility) => void;
  /** "Search this area" → the map's CURRENT center (re-query around it). */
  onSearchArea: (center: FacilityMapCenter) => void;
  /** Optional initial zoom. */
  zoom?: number;
  /**
   * Injectable maps seam (key resolver + script loader). Defaults to the shared
   * AddressAutocomplete loader — the SAME loader, no new map lib. Exposed as
   * props so a consumer can pass the already-resolved barrel functions (the
   * caller's module, unlike this one, can be intercepted by a barrel mock), and
   * so tests can stub the seam without a real <script> in jsdom.
   */
  getGoogleMapsApiKey?: GetApiKeyFn;
  loadGoogleMaps?: LoadMapsFn;
}

export type FacilityMapLoadState = "loading" | "ready" | "unavailable";

export interface FacilityMapMarkerContext {
  facility: FacilityMapFacility;
  marker: google.maps.Marker;
  map: google.maps.Map;
}

export interface FacilityMapControllerOptions {
  facilities: FacilityMapFacility[];
  center: FacilityMapCenter;
  onMarkerActivate: (context: FacilityMapMarkerContext) => void;
  onSearchArea: (center: FacilityMapCenter) => void;
  zoom?: number;
  /** Google Maps style rules, e.g. a dark basemap. Applied at creation. */
  styles?: google.maps.MapTypeStyle[];
  /** Labelled pill pins instead of numbered markers. */
  pillColors?: FacilityMapPillColors;
  /**
   * Touch and button behaviour. "app" is a full-screen map like Google Maps:
   * one-finger pan, pinch and double-tap zoom, no camera control, and zoom
   * buttons only where `zoomButtons` is set (desktop). "embedded" (default)
   * keeps Google's two-finger pan so the surrounding page still scrolls.
   */
  interaction?: "app" | "embedded";
  zoomButtons?: boolean;
  /**
   * The viewer's own position, drawn as a blue dot. Display only: the caller
   * decides whether it may be shown, and it is never sent anywhere.
   */
  userLocation?: FacilityMapCenter | null;
  getGoogleMapsApiKey?: GetApiKeyFn;
  loadGoogleMaps?: LoadMapsFn;
}

const PILL_HEIGHT = 28;

/** Size/Point exist on the full Maps script; without them, use plain pins. */
function canDrawIcons(): boolean {
  return typeof google.maps.Size === "function" && typeof google.maps.Point === "function";
}

/** An SVG pill (with a small pointer) sized to its label, as a marker icon. */
function pillIcon(text: string, fill: string, stroke: string): google.maps.Icon {
  const width = Math.round(text.length * 7 + 24);
  const mid = width / 2;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${PILL_HEIGHT + 6}">` +
    `<rect x="1" y="1" width="${width - 2}" height="${PILL_HEIGHT - 2}" rx="${(PILL_HEIGHT - 2) / 2}" fill="${fill}" stroke="${stroke}"/>` +
    `<path d="M${mid - 5} ${PILL_HEIGHT - 1.5}L${mid} ${PILL_HEIGHT + 5}L${mid + 5} ${PILL_HEIGHT - 1.5}Z" fill="${fill}"/>` +
    `</svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(width, PILL_HEIGHT + 6),
    anchor: new google.maps.Point(mid, PILL_HEIGHT + 5),
    labelOrigin: new google.maps.Point(mid, PILL_HEIGHT / 2),
  };
}

const USER_DOT_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22">` +
  `<circle cx="11" cy="11" r="10" fill="#4285F4" fill-opacity="0.25"/>` +
  `<circle cx="11" cy="11" r="6" fill="#4285F4" stroke="#fff" stroke-width="2"/></svg>`;

export interface FacilityMapController {
  containerRef: RefObject<HTMLDivElement | null>;
  loadState: FacilityMapLoadState;
  handleSearchArea: () => void;
  /** Smoothly recenter the map (e.g. on the viewer's location). */
  panTo: (center: FacilityMapCenter) => void;
}

/**
 * Headless Google Maps controller shared by audience-specific renderers. It is
 * the only owner of map loading, recentering, coordinate filtering, marker
 * lifecycle, and current-center reads. Renderers own toolbar/fallback/popup UI.
 */
export function useFacilityMapController({
  facilities,
  center,
  onMarkerActivate,
  onSearchArea,
  zoom = 11,
  styles,
  pillColors,
  interaction = "embedded",
  zoomButtons = true,
  userLocation,
  getGoogleMapsApiKey = defaultGetGoogleMapsApiKey,
  loadGoogleMaps = defaultLoadGoogleMaps,
}: FacilityMapControllerOptions): FacilityMapController {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const [loadState, setLoadState] = useState<FacilityMapLoadState>("loading");
  const onMarkerActivateRef = useRef(onMarkerActivate);
  onMarkerActivateRef.current = onMarkerActivate;
  const onSearchAreaRef = useRef(onSearchArea);
  onSearchAreaRef.current = onSearchArea;

  useEffect(() => {
    let cancelled = false;
    let authFailed = false;
    const apiKey = getGoogleMapsApiKey();
    if (!apiKey) {
      setLoadState("unavailable");
      return;
    }

    const unsubscribeFromMapsAuthFailure = subscribeToMapsAuthFailure(() => {
      if (cancelled) return;
      authFailed = true;
      mapRef.current = null;
      setLoadState("unavailable");
    });

    loadGoogleMaps(apiKey)
      .then(() => {
        if (cancelled || authFailed) return;
        const container = containerRef.current;
        if (!container || typeof google === "undefined" || !google.maps) {
          setLoadState("unavailable");
          return;
        }
        mapRef.current = new google.maps.Map(container, {
          center: { lat: center.latitude, lng: center.longitude },
          zoom,
          disableDefaultUI: false,
          ...(interaction === "app"
            ? { gestureHandling: "greedy" as const, cameraControl: false, zoomControl: zoomButtons, clickableIcons: false }
            : {}),
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          ...(styles ? { styles } : {}),
        });
        setLoadState("ready");
      })
      .catch(() => {
        if (!cancelled) setLoadState("unavailable");
      });

    return () => {
      cancelled = true;
      unsubscribeFromMapsAuthFailure();
    };
    // The map is instantiated once. A separate effect handles recentering.
  }, []);

  useEffect(() => {
    if (loadState !== "ready" || !mapRef.current) return;
    mapRef.current.setCenter({ lat: center.latitude, lng: center.longitude });
  }, [center.latitude, center.longitude, loadState]);

  useEffect(() => {
    if (loadState !== "ready" || !mapRef.current) return;
    const map = mapRef.current;

    for (const marker of markersRef.current) marker.setMap(null);
    markersRef.current = [];

    let placed = 0;
    for (const facility of facilities) {
      if (
        facility.latitude === undefined ||
        facility.latitude === null ||
        facility.longitude === undefined ||
        facility.longitude === null
      ) {
        continue;
      }
      placed += 1;
      const text = facility.pinLabel?.trim() || facility.name;
      const highlight = Boolean(facility.highlight);
      const marker = new google.maps.Marker({
        position: { lat: facility.latitude, lng: facility.longitude },
        map,
        title: facility.name,
        ...(pillColors && canDrawIcons()
          ? {
              icon: pillIcon(
                text,
                highlight ? pillColors.highlightFill : pillColors.fill,
                highlight ? pillColors.highlightFill : pillColors.stroke,
              ),
              label: {
                text,
                color: highlight ? pillColors.highlightText : pillColors.text,
                fontSize: "12px",
                fontWeight: "700",
              },
              zIndex: highlight ? 2 : 1,
            }
          : { label: String(placed) }),
      });
      marker.addListener("click", () => {
        onMarkerActivateRef.current({ facility, marker, map });
      });
      markersRef.current.push(marker);
    }

    return () => {
      for (const marker of markersRef.current) marker.setMap(null);
      markersRef.current = [];
    };
  }, [facilities, loadState, pillColors]);

  // Follow a light/dark switch made while the map is open.
  useEffect(() => {
    if (loadState !== "ready" || !mapRef.current) return;
    const map = mapRef.current;
    if (typeof map.setOptions === "function") map.setOptions({ styles: styles ?? [] });
  }, [styles, loadState]);

  const userMarkerRef = useRef<google.maps.Marker | null>(null);
  useEffect(() => {
    if (loadState !== "ready" || !mapRef.current) return;
    if (!userLocation || !canDrawIcons()) {
      userMarkerRef.current?.setMap(null);
      userMarkerRef.current = null;
      return;
    }
    const position = { lat: userLocation.latitude, lng: userLocation.longitude };
    if (userMarkerRef.current) {
      userMarkerRef.current.setPosition(position);
      return;
    }
    userMarkerRef.current = new google.maps.Marker({
      position,
      map: mapRef.current,
      title: "Your location",
      clickable: false,
      zIndex: 0,
      icon: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(USER_DOT_SVG)}`,
        scaledSize: new google.maps.Size(22, 22),
        anchor: new google.maps.Point(11, 11),
      },
    });
  }, [userLocation?.latitude, userLocation?.longitude, loadState]);

  useEffect(
    () => () => {
      userMarkerRef.current?.setMap(null);
      userMarkerRef.current = null;
    },
    [],
  );

  function panTo(target: FacilityMapCenter) {
    mapRef.current?.panTo({ lat: target.latitude, lng: target.longitude });
  }

  function handleSearchArea() {
    const centerPoint = mapRef.current?.getCenter();
    if (!centerPoint) return;
    onSearchAreaRef.current({
      latitude: centerPoint.lat(),
      longitude: centerPoint.lng(),
    });
  }

  return { containerRef, loadState, handleSearchArea, panTo };
}
